/**
 * Logika řazení tabulky základní části.
 * Během sezóny: body, rozdíl gólů, vstřelené, obdržené.
 * Po skončení sezóny: body, vzájemné zápasy (head-to-head), pak globální kritéria.
 */

/**
 * Zkontroluje, zda je základní část sezóny dokončena (všechny zápasy odehrány).
 * @param {Array<{status: string}>} matches - Všechny zápasy základní části pro daný ročník
 * @returns {boolean}
 */
export function isSeasonComplete(matches) {
  if (!matches || matches.length === 0) return true;
  return matches.every((m) => m.status === 'played');
}

/**
 * Porovnání dvou týmů podle globálních kritérií (používáno během sezóny i v mini-tabulce).
 * Pořadí: body desc, rozdíl gólů desc, vstřelené desc, obdržené asc, název asc.
 */
function compareByGlobalCriteria(a, b) {
  if (b.points !== a.points) return b.points - a.points;
  const aDiff = a.goalsFor - a.goalsAgainst;
  const bDiff = b.goalsFor - b.goalsAgainst;
  if (bDiff !== aDiff) return bDiff - aDiff;
  if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
  if (a.goalsAgainst !== b.goalsAgainst) return a.goalsAgainst - b.goalsAgainst;
  return (a.name || '').localeCompare(b.name || '', 'cs');
}

/**
 * Vrátí pouze vzájemné odehrané zápasy mezi zadanými týmy.
 */
function getMutualMatches(matches, teamIds) {
  const set = new Set(teamIds);
  return matches.filter(
    (m) =>
      m.status === 'played' &&
      set.has(m.home_team_id) &&
      set.has(m.away_team_id)
  );
}

/**
 * Sestaví mini-tabulku (statistiky) pouze ze vzájemných zápasů daných týmů.
 */
function buildMiniTable(teamIds, mutualMatches) {
  const table = {};
  teamIds.forEach((id) => {
    table[id] = {
      id,
      points: 0,
      goalsFor: 0,
      goalsAgainst: 0,
      name: '',
    };
  });
  mutualMatches.forEach((m) => {
    const home = table[m.home_team_id];
    const away = table[m.away_team_id];
    if (!home || !away) return;
    home.goalsFor += m.home_score;
    home.goalsAgainst += m.away_score;
    away.goalsFor += m.away_score;
    away.goalsAgainst += m.home_score;
    if (m.home_score > m.away_score) {
      home.points += 3;
    } else if (m.home_score < m.away_score) {
      away.points += 3;
    } else {
      home.points += 1;
      away.points += 1;
    }
  });
  return Object.values(table);
}

/**
 * Seřadí skupinu týmů se stejným počtem bodů podle vzájemných zápasů,
 * poté podle globálních kritérií. Deterministické řazení.
 */
function sortTiedGroup(group, allMatches, nameById) {
  const teamIds = group.map((t) => t.id);
  const mutualMatches = getMutualMatches(allMatches, teamIds);

  if (mutualMatches.length === 0) {
    return [...group].sort((a, b) => compareByGlobalCriteria(a, b));
  }

  const miniTable = buildMiniTable(teamIds, mutualMatches);
  miniTable.forEach((row) => {
    row.name = nameById[row.id] || '';
  });
  miniTable.sort((a, b) => compareByGlobalCriteria(a, b));

  const orderById = {};
  miniTable.forEach((row, index) => {
    orderById[row.id] = index;
  });

  return [...group].sort((a, b) => {
    const rankA = orderById[a.id] ?? 999;
    const rankB = orderById[b.id] ?? 999;
    return rankA - rankB;
  });
}

/**
 * Seřadí tabulku základní části.
 * - Během sezóny: body, rozdíl gólů, vstřelené, obdržené, název.
 * - Po skončení sezóny: body, pak pro shodné body head-to-head (mini-tabulka),
 *   pak globální kritéria (rozdíl, vstřelené, obdržené, název).
 *
 * @param {Array<{id: number, name: string, played: number, won: number, drawn: number, lost: number, goalsFor: number, goalsAgainst: number, points: number}>} tableArray
 * @param {Array<{home_team_id: number, away_team_id: number, home_score: number, away_score: number, status: string}>} matches
 * @returns {Array} Seřazená tabulka
 */
export function sortLeagueTable(tableArray, matches) {
  if (!tableArray || tableArray.length === 0) return tableArray;

  const nameById = {};
  tableArray.forEach((t) => {
    nameById[t.id] = t.name;
  });

  const byPoints = [...tableArray].sort((a, b) => b.points - a.points);
  const seasonComplete = isSeasonComplete(matches);

  if (!seasonComplete) {
    console.log('[leagueTableSort] Během sezóny – řazení podle: body, rozdíl gólů, vstřelené, obdržené');
    return byPoints.sort((a, b) => compareByGlobalCriteria(a, b));
  }

  console.log('[leagueTableSort] Po skončení sezóny – řazení s head-to-head pro shodné body');
  const groups = [];
  let i = 0;
  while (i < byPoints.length) {
    const points = byPoints[i].points;
    let j = i;
    while (j < byPoints.length && byPoints[j].points === points) j++;
    groups.push(byPoints.slice(i, j));
    i = j;
  }

  const result = [];
  for (const group of groups) {
    if (group.length === 1) {
      result.push(group[0]);
    } else {
      result.push(...sortTiedGroup(group, matches, nameById));
    }
  }

  return result;
}
